import {
  createRootRoute,
  createRoute,
  createRouter,
  Link as RouterLink,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { IconSheet, LinkProvider, type LinkProps } from './ui';
import { HomeRoute } from './routes/home';
import { LegalRoute, PrivacyRoute, TermsRoute } from './routes/legal';
import { NotFound } from './routes/shared';

function splitHref(href: string): {
  path: string;
  search: Record<string, string> | undefined;
  hash: string | undefined;
} {
  const hashAt = href.indexOf('#');
  const hash = hashAt < 0 ? undefined : href.slice(hashAt + 1);
  const withoutHash = hashAt < 0 ? href : href.slice(0, hashAt);

  const queryAt = withoutHash.indexOf('?');
  if (queryAt < 0) return { path: withoutHash, search: undefined, hash };

  const search: Record<string, string> = {};
  for (const [key, value] of new URLSearchParams(withoutHash.slice(queryAt + 1))) {
    search[key] = value;
  }

  return { path: withoutHash.slice(0, queryAt), search, hash };
}

const RouterLinkAdapter = ({
  href,
  children,
  className,
  style,
  title,
  ref,
  onPointerMove,
  onPointerLeave,
  onPointerEnter,
  onPointerDown,
  ...rest
}: LinkProps) => {
  const { path, search, hash } = splitHref(href);

  return (
    <RouterLink
      to={path as never}
      {...(search ? { search: search as never } : {})}
      {...(hash ? { hash } : {})}
      className={className}
      style={style}
      title={title}
      ref={ref as never}
      onPointerMove={onPointerMove}
      onPointerLeave={onPointerLeave}
      onPointerEnter={onPointerEnter}
      onPointerDown={onPointerDown}
      {...(rest['data-tilt'] === undefined ? {} : { 'data-tilt': rest['data-tilt'] })}
      {...(rest['aria-current'] === undefined ? {} : { 'aria-current': rest['aria-current'] })}
      {...(rest['aria-label'] === undefined ? {} : { 'aria-label': rest['aria-label'] })}
    >
      {children}
    </RouterLink>
  );
};

function Shell(): React.ReactElement {
  return (
    <LinkProvider component={RouterLinkAdapter}>
      <IconSheet />
      <Outlet />
    </LinkProvider>
  );
}

const rootRoute = createRootRoute({
  component: Shell,
  notFoundComponent: NotFound,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: HomeRoute,
});

/**
 * A game, which is the same room with one case out of the pile.
 *
 * **There is no page behind this and there must not be one.** `/g/<slug>` used
 * to render a document — a hero, a gallery, a description — and the pile linked
 * out to it, which made the case a menu item for the real page. The case *is*
 * the page: everything that was on the document is printed on the insert and the
 * disc, and this route only says which one is open.
 *
 * The component is the same one `/` renders, so opening a case is a navigation
 * rather than a state change and a shared link arrives at a room with that case
 * already in the reader's hands. Nothing remounts between the two: TanStack
 * keeps the component when only the params change, which is what lets the case
 * animate out of the pile instead of the scene rebuilding around it.
 */
const gameRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/g/$slug',
  component: HomeRoute,
});

const legalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/legal',
  component: LegalRoute,
});

const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/legal/terms',
  component: TermsRoute,
});

const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/legal/privacy',
  component: PrivacyRoute,
});

const browseRedirect = createRoute({
  getParentRoute: () => rootRoute,
  path: '/browse',
  beforeLoad: () => {
    throw redirect({ to: '/' as never });
  },
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  gameRoute,
  legalRoute,
  termsRoute,
  privacyRoute,
  browseRedirect,
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
